import React, { memo, useContext, useMemo, useCallback } from 'react'
import { Background, SolidButton, ScreenContext, mobileBreakpoint, tabletBreakpoint } from 'react-components'

import { BackgroundImageTransport, HeroSectionTransport } from '@common/transports/deprecated-home-page-transport'
import {
    Container,
    BackgroundShadowContainer,
    BackgroundChildContainer,
    ButtonContainer,
    ButtonContentContainer,
    HeroSectionContainer,
    LogoutButton,
    Subtitle,
    TitleContainer,
    TrustBarContainer,
} from './hero-section-styles'
import { TrustBarItem } from './trustbar-item'
import { AnalyticsService } from '@src/services'
import { homePageEventCategories } from '@src/services/analytics-service/categories'

interface HeroSectionProps {
    heroSection: HeroSectionTransport
    isLoggedIn: boolean
}

const _getImageSourceSet = (backgroundImage: BackgroundImageTransport) => [
    { src: backgroundImage.desktop.small, mediaCondition: 'max-width: 1399px' },
    { src: backgroundImage.tablet.medium, mediaCondition: `max-width: ${tabletBreakpoint.max}px` },
    { src: backgroundImage.tablet.small, mediaCondition: `max-width: ${mobileBreakpoint.max}px` },
    { src: backgroundImage.mobile.medium, mediaCondition: 'max-width: 364px' },
]

const HeroSection: React.FC<HeroSectionProps> = ({
    heroSection: { backgroundImage, trustbarItems, id, title, subtitle, dashboardButton, registerButton, logoutButton },
    isLoggedIn,
}) => {
    const ctaButtonToRender = isLoggedIn ? dashboardButton : registerButton
    const _imageSourceSet = useMemo(() => _getImageSourceSet(backgroundImage), [backgroundImage])
    const { isDesktop } = useContext(ScreenContext)

    const _handleCTAButtonClick = useCallback(() => {
        if (ctaButtonToRender === registerButton && ctaButtonToRender.eventLabel) {
            AnalyticsService.trackClickEvent(homePageEventCategories.HERO_SECTION, ctaButtonToRender.eventLabel)
        }
    }, [ctaButtonToRender, registerButton])

    return (
        // Adjusted Margin in styles file according to header
        <HeroSectionContainer id={id}>
            <Background baseImageSource={backgroundImage.desktop.medium} imageSourceSet={_imageSourceSet}>
                <BackgroundShadowContainer>
                    {isDesktop ? (
                        <BackgroundChildContainer>
                            <TrustBarContainer>
                                {trustbarItems.map(trustbarItem => (
                                    <TrustBarItem key={trustbarItem.text} trustbarItem={trustbarItem} />
                                ))}
                            </TrustBarContainer>
                            <Container>
                                <TitleContainer>{title}</TitleContainer>
                                <ButtonContainer>
                                    <SolidButton
                                        href={ctaButtonToRender.url}
                                        padding="0"
                                        onClick={_handleCTAButtonClick}
                                    >
                                        <ButtonContentContainer>{ctaButtonToRender.text}</ButtonContentContainer>
                                    </SolidButton>
                                </ButtonContainer>
                                <Subtitle>
                                    {isLoggedIn ? (
                                        <LogoutButton href={logoutButton.url}>{logoutButton.text}</LogoutButton>
                                    ) : (
                                        subtitle
                                    )}
                                </Subtitle>
                            </Container>
                        </BackgroundChildContainer>
                    ) : (
                        <BackgroundChildContainer>
                            <TitleContainer>{title}</TitleContainer>
                            <Container>
                                <TrustBarContainer>
                                    {trustbarItems.map(trustbarItem => (
                                        <TrustBarItem key={trustbarItem.text} trustbarItem={trustbarItem} />
                                    ))}
                                </TrustBarContainer>
                                <ButtonContainer>
                                    <SolidButton
                                        href={ctaButtonToRender.url}
                                        padding="0"
                                        onClick={_handleCTAButtonClick}
                                    >
                                        <ButtonContentContainer>{ctaButtonToRender.text}</ButtonContentContainer>
                                    </SolidButton>
                                </ButtonContainer>
                                <Subtitle>
                                    {isLoggedIn ? (
                                        <LogoutButton href={logoutButton.url}>{logoutButton.text}</LogoutButton>
                                    ) : (
                                        subtitle
                                    )}
                                </Subtitle>
                            </Container>
                        </BackgroundChildContainer>
                    )}
                </BackgroundShadowContainer>
            </Background>
        </HeroSectionContainer>
    )
}

export default memo(HeroSection)
